import styled from 'styled-components';
import NavSidebarHeader from './NavSidebarHeader';
import NavSidebarFooter from './NavSidebarFooter';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { clickEditViewButton, clickDeleteViewButton, viewCreationCompleted, viewDeleted } from '../../custom-views/constants/events';
import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentViewId } from '../../thread-list/selectors/getCurrentViewId';
import { getCurrentInboxId } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { getUserId } from '../../selectors/userSelectors/getUserId';
import { getUserHasCustomViewsAccess } from '../../selectors/userSelectors/getUserHasCustomViewsAccess';
import { THREAD_LIST_SWITCHING_MARKER } from '../../performance-metrics/constants/performanceConstants';
import { setCurrentThreadList } from '../../thread-list/actions/setCurrentThreadList';
import { getCreatedAt, getViewUsageTrackingName, isNewlyCreatedProcessingView } from 'find-and-filter-data/views-schema/public';
import { getChangedView, getCustomViewDragAndDrop } from 'conversations-inbox-lib/usage-tracking/inboxInteractionEvents';
import HandleNewFeatureTourStepAccess from './HandleNewFeatureTourStepAccess';
import { useViews, useView } from 'find-and-filter-data/views-data/public';
import { CreateCustomViewWizard } from '../../custom-views/components/CreateCustomViewWizard';
import { EditCustomViewWizard } from '../../custom-views/components/EditCustomViewWizard';
import { DeleteCustomViewModal } from '../../custom-views/components/DeleteCustomViewModal';
import { getDefaultViewFromViews } from 'conversations-inbox-lib/views/public/operators';
import I18n from 'I18n';
import NavSidebarLinksListInbox from './NavSidebarLinksListInbox';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SidebarContent = styled.nav.withConfig({
  displayName: "InboxNavigationSidebar__SidebarContent",
  componentId: "sc-1ds8i52-0"
})(["height:100%;width:100%;overflow-y:auto;padding:8px 0;padding-left:12px;"]);
const handleSortTracking = () => {
  trackEvent(getCustomViewDragAndDrop());
};

/**
 * @description The sidebar to control navigation through the inbox. Navigates
 * between views and includes some agent actions.
 */
const InboxNavigationSidebar = () => {
  const hasCustomViewsAccess = useSelector(getUserHasCustomViewsAccess);
  const dispatch = useDispatch();
  const inboxId = useSelector(getCurrentInboxId);
  const [showCustomViewPanel, setShowCustomViewPanel] = useState(false);
  const {
    views
  } = useViews(inboxId);
  const [viewIdToEdit, setViewIdToEdit] = useState(null);
  const [viewIdToDelete, setViewIdToDelete] = useState(null);
  const handleEditViewClick = useCallback((event, viewId) => {
    event.stopPropagation();
    setViewIdToEdit(viewId);
    trackEvent(clickEditViewButton());
  }, [setViewIdToEdit]);
  const handleDeleteCustomView = useCallback((event, viewId) => {
    event.stopPropagation();
    setViewIdToDelete(viewId);
    trackEvent(clickDeleteViewButton());
  }, [setViewIdToDelete]);
  const handleViewMemberClick = useCallback((disabled, selected, view) => {
    if (!disabled && !selected) {
      const viewName = getViewUsageTrackingName(view);
      trackEvent(getChangedView({
        view: viewName
      }));
      performance.mark(`${THREAD_LIST_SWITCHING_MARKER}-start`);
      dispatch(setCurrentThreadList(view));
    }
  }, [dispatch]);
  const handleTrackViewTracking = useCallback(view => {
    trackEvent(viewCreationCompleted({
      isNewView: isNewlyCreatedProcessingView(view),
      viewCreatedAt: getCreatedAt(view)
    }));
  }, []);
  const customViewToEdit = useView(inboxId || null, viewIdToEdit || null);
  const customViewToDelete = useView(inboxId || null, viewIdToDelete || null);
  const currentViewId = useSelector(getCurrentViewId);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavSidebarHeader, {}), /*#__PURE__*/_jsx(SidebarContent, {
      children: /*#__PURE__*/_jsx(NavSidebarLinksListInbox, {
        views: views,
        handleEditViewClick: handleEditViewClick,
        handleDeleteViewClick: handleDeleteCustomView,
        handleSortTracking: handleSortTracking,
        inboxId: inboxId,
        userId: useSelector(getUserId),
        viewId: useSelector(getCurrentViewId),
        hasCustomViewsAccess: hasCustomViewsAccess,
        handleViewMemberClick: handleViewMemberClick,
        handleTrackViewTracking: handleTrackViewTracking,
        HandleNewFeatureTourStep: HandleNewFeatureTourStepAccess(hasCustomViewsAccess)
      })
    }), /*#__PURE__*/_jsx(NavSidebarFooter, {
      onCreateCustomViewClick: () => setShowCustomViewPanel(true)
    }), showCustomViewPanel && /*#__PURE__*/_jsx(CreateCustomViewWizard, {
      onReject: () => setShowCustomViewPanel(false),
      onSuccess: () => setShowCustomViewPanel(false)
    }), customViewToEdit && /*#__PURE__*/_jsx(EditCustomViewWizard, {
      customView: customViewToEdit,
      onReject: () => setViewIdToEdit(null),
      onSuccess: () => setViewIdToEdit(null)
    }), viewIdToDelete && /*#__PURE__*/_jsx(DeleteCustomViewModal, {
      customView: customViewToDelete,
      onConfirm: () => {
        const shouldSwitchViewOnDelete = currentViewId === viewIdToDelete;
        if (shouldSwitchViewOnDelete) {
          dispatch(setCurrentThreadList(getDefaultViewFromViews(views)));
        }
      },
      onReject: () => setViewIdToDelete(null),
      onSuccess: deletedCustomView => {
        setViewIdToDelete(null);
        trackEvent(viewDeleted({
          viewCreatedAt: getCreatedAt(deletedCustomView)
        }));
        dispatch({
          /**
           * This action type is entirely arbitrary and just needs to fulfill
           * the redux standard action convention.
           */
          type: 'DELETE_VIEW_SUCCESS_NOTIFICATION',
          meta: {
            notification: {
              titleText: I18n.text('conversations-inbox-ui.custom-views.deleteModal.notification.titleText'),
              message: I18n.text('conversations-inbox-ui.custom-views.deleteModal.notification.message'),
              type: 'success'
            }
          }
        });
      }
    })]
  });
};
InboxNavigationSidebar.displayName = 'InboxNavigationSidebar';
export default InboxNavigationSidebar;